import React, { useState } from 'react';
import { usePortal } from '../../contexts/PortalContext';
import type { Deliverable } from '../../types/portal';
import { BUTTON_QUIET } from './adminStyles';

/**
 * The current version reads at a glance; earlier ones stay folded away until
 * they are wanted. Nothing is lost — every version and its visibility control
 * is still here on expanding.
 */
export function VersionHistory({
  deliverable
}: {
  deliverable: Deliverable;
}) {
  const {
    toggleVersionVisibility,
    notify
  } = usePortal();
  const [expanded, setExpanded] = useState(false);
  const earlier = deliverable.versions;
  return <section aria-labelledby={`versions-${deliverable.id}`} data-id="element-405" data-mp="1294mede8z00t">
      <h4 id={`versions-${deliverable.id}`} className="label border-b border-ruleDark pb-4 text-paper/40" data-id="element-406" data-mp="12apbhnhslqra">
        Version History
      </h4>

      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-3 border-b border-ruleDark py-5" data-id="element-407" data-mp="2wmvpeu22mlna">
        <span className="flex items-baseline gap-5" data-id="element-408" data-mp="31jcogjat6kf5">
          <span className="font-display text-2xl leading-none" data-id="element-409" data-mp="2b493001t7jqf">{deliverable.version || '—'}</span>
          <span className="label border border-paper bg-paper px-2.5 py-1.5 text-ink" data-id="element-410" data-mp="266pjakmceer3">
            Current Version
          </span>
        </span>
        <span className="label-sm text-paper/40" data-id="element-411" data-mp="2kwwt6byaapg6">
          {deliverable.status}
          {deliverable.versionDate !== '—' ? ` · ${deliverable.versionDate}` : ''}
        </span>
      </div>

      {earlier.length > 0 && <button type="button" onClick={() => setExpanded(current => !current)} aria-expanded={expanded} className="label-sm mt-5 text-paper/40 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-paper" data-id="element-412" data-mp="1n7bnry8ysvyh">
          {expanded ? 'Hide previous versions' : `${earlier.length} previous version${earlier.length === 1 ? '' : 's'} ›`}
        </button>}

      {expanded && <ul className="m-0 mt-5 list-none p-0" data-id="element-413" data-mp="38mhidcl16ixw">
          {earlier.map(entry => <li key={entry.id} className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-3 border-b border-ruleDark py-5" data-id="element-414" data-mp="25993t0e15glp">
              <span className="flex items-baseline gap-5" data-id="element-415" data-mp="2f8zv6t7anfhk">
                <span className="font-display text-2xl leading-none text-paper/70" data-id="element-416" data-mp="2j909mdue7qom">
                  {entry.version}
                </span>
                <span className="label-sm text-paper/40" data-id="element-417" data-mp="1mkavmylvzi7x">
                  {entry.label} · {entry.date}
                  {entry.changeCount ? ` · ${entry.changeCount} change${entry.changeCount === 1 ? '' : 's'}` : ''}
                </span>
              </span>

              <span className="flex items-center gap-6" data-id="element-418" data-mp="20bz3uqt0lmjm">
                <button type="button" onClick={() => notify(entry.driveUrl ? `Simulated · would open ${entry.version} in Google Drive` : `Simulated · ${entry.version} preview`)} className={BUTTON_QUIET} data-id="element-419" data-mp="13t1hgc1685a0">
                  Open
                </button>
                <button type="button" onClick={() => {
            toggleVersionVisibility(deliverable.id, entry.id);
            notify(entry.visibleToClient ? `${entry.version} hidden from client` : `${entry.version} shown to client under Previous Versions`);
          }} aria-pressed={entry.visibleToClient} className={['label border px-3 py-2 transition-colors duration-150 ease-out', entry.visibleToClient ? 'border-paper bg-paper text-ink' : 'border-paper/30 text-paper/40 hover:border-paper/60'].join(' ')} data-id="element-420" data-mp="r82y76h6hyyg">
                  {entry.visibleToClient ? 'Shown in Client Portal' : 'Internal'}
                </button>
              </span>
            </li>)}
        </ul>}
    </section>;
}